import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRequest } from '@/hooks';
import OnboardingTooltip from './OnboardingTooltip';
import CreditLabel from '../pages/home/components/CreditLabel';
import AiCreateButton from '@/pages/ai/components/AiCreateButton';

type OnboardingStep = 1 | 2;

interface AiCreditOnboardingTemplateProps {
  isVisible: boolean;
  onComplete: () => void;
}

interface TooltipPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  transform?: string;
}

function AiCreditOnboardingTemplate({ isVisible, onComplete }: AiCreditOnboardingTemplateProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  const { appId } = useParams<AppParam>();

  const { data: creditValues } = useRequest<CommonResponseModel<RemainCreditInfoResponseModel>>(
    `/apps/${appId}/credit`
  );

  const totalCredit = creditValues
    ? creditValues.data?.freeCredit + creditValues.data?.paidCredit
    : 0;

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    setCurrentStep(1);
  };

  const getTooltipContent = (step: OnboardingStep) => {
    switch (step) {
      case 1:
        return {
          title: '크레딧 활용하기',
          content: '크레딧으로 AI 이미지, BGM을 AI로 생성할 수 있어요.',
          position: { top: '56px', right: '300px' } as TooltipPosition,
          arrowDirection: 'up' as const
        };
      case 2:
        return {
          title: 'AI 이미지 만들기',
          content:
            '미션 이름, 한 줄 설명에 맞춰 이미지를 자동으로 만들어요.\n(만들 때 크레딧이 소모돼요.)',
          position: { top: '379px', left: '960px', transform: 'translateX(-50%)' },
          arrowDirection: 'down' as const
        };
      default:
        return null;
    }
  };

  const tooltipData = getTooltipContent(currentStep);

  if (!tooltipData) return null;

  return (
    <div css={overlayStyle}>
      <div css={dimmedBackground} />

      {/* Credit Label - Step 1에서만 하이라이트 */}
      {currentStep === 1 && (
        <div css={highlightedCreditStyle}>
          <div css={highlightBorderStyle}>
            <CreditLabel credit={totalCredit} />
          </div>
        </div>
      )}

      {/* AI 이미지 생성 버튼 영역 - Step 2에서만 하이라이트 */}
      {currentStep === 2 && (
        <div css={highlightedAiButtonStyle}>
          <div css={highlightBorderStyle}>
            <AiCreateButton creditValue={10} width="138px" height="33px" />
          </div>
        </div>
      )}

      {/* Tooltip */}
      <div css={tooltipPositionStyle(tooltipData.position)}>
        <OnboardingTooltip
          title={tooltipData.title}
          content={tooltipData.content}
          arrowDirection={tooltipData.arrowDirection}
          onNext={handleNext}
          onPrevious={currentStep === 2 ? handlePrevious : undefined}
          showPrevious={currentStep === 2}
          nextButtonText={currentStep === 2 ? '완료' : '다음'}
        />
      </div>
    </div>
  );
}

export default AiCreditOnboardingTemplate;

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  z-index: 9999;
  pointer-events: auto;
`;

const dimmedBackground = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
`;

const highlightBorderStyle = css`
  position: relative;
  border-radius: 12px;
`;

const highlightedCreditStyle = css`
  position: absolute;
  top: 13px;
  right: 405px;
  z-index: 10001;
`;

const highlightedAiButtonStyle = css`
  position: absolute;
  top: 591px;
  left: 963px;
  transform: translate(-50%, -50%);
  z-index: 10001;
`;

const tooltipPositionStyle = (position: TooltipPosition) => css`
  position: absolute;
  z-index: 10002;
  ${position.top ? `top: ${position.top};` : ''}
  ${position.left ? `left: ${position.left};` : ''}
  ${position.right ? `right: ${position.right};` : ''}
  ${position.bottom ? `bottom: ${position.bottom};` : ''}
  ${position.transform ? `transform: ${position.transform};` : ''}
`;
